import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import Utils, { BrValidators } from 'src/app/helpers/utils';
import { UtilsValidators } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ToastService } from 'src/app/services/toast/toast.service';

@Component({
  selector: 'app-dashboard-invites',
  templateUrl: './dashboard-invites.component.html',
  styleUrls: ['./dashboard-invites.component.scss'],
})
export class DashboardInvitesComponent implements OnInit {
  permissionObj: PermissionValues = {
    canView: false,
    canAdd: false,
    canChange: false,
    canRemove: false,
    canFilter: false,
    canViewDetails: false,
    canAddAssignor: false,
    canAddShareholder: false,
    canAddProvider: false,
  };

  form = this.formBuilder.group({
    unified_role: [null, [Validators.required]], // Papel unificado: "assignor-PF", "shareholder-PJ", etc.
    name: [null, [Validators.required]], // Validação fullName será adicionada dinamicamente para PF
    agent: [null, []],
    document: [null, [Validators.required, Utils.validateCpfCnpj]], // Aceita CPF ou CNPJ
    email: [null, [Validators.required, UtilsValidators.email]],
    fund: [null, [Validators.required]],
    shareholder_fi: [false, []],
  });

  modal2Form = this.formBuilder.group({
    document: [null, [Validators.required]],
  });

  filterForm = this.formBuilder.group({
    name: [null, []],
    email: [null, [Validators.email]],
    document: [null, [Utils.validateCpfCnpj]],
    status: [null, []],
  });

  loading = true;
  sendingInvite = false;
  showFilterModal = false;
  sortButtonExpanded = false;
  offset = 0;
  showing_to = 0;
  count = 0;
  resultsPerPage = 10;
  invites: InviteProps[] = [];
  filteredInvites: InviteProps[] = this.invites;
  newInvite = false;
  dataFilters: any = null;
  editing = false;
  inviteId = null;
  documentMask: string = 'CPF_CNPJ'; // Máscara padrão
  checkingInvites = false; // Flag para controlar verificação de convites
  documentInviteError: string = null; // Mensagem de erro do documento
  showOtherFields = false; // Flag para controlar exibição dos outros campos após verificação do documento
  documentVerified = false; // Flag para indicar se o documento foi verificado
  usedInviteTypes: Set<string> = new Set(); // Tipos de convites usados (assignor, shareholder, provider)
  documentPersonType: string | null = null; // Person type do documento baseado nos convites usados
  existingInviteData: InviteProps | null = null; // Dados do convite usado existente

  // Controle de validação de documento (similar ao dashboard-user-list)
  checkingDocument: boolean = false;
  documentExists: boolean | null = null;
  availablePapers: any[] = [];
  currentPapers: any[] = [];
  userName: string = '';

  // Controle de colaborador
  isCollaborator: boolean = false;
  collaboratorInfo: any = null;
  collaboratorMessage: string = '';

  // Controle de aprovação pendente
  hasPendingApproval: boolean = false;
  pendingApprovalRole: string = '';

  // Controle de convite já enviado
  hasInviteSent: boolean = false;
  inviteStatus: string = '';
  invitedData: any = null; // Dados completos do convite enviado (incluindo sender_name)

  // Controle de role_draft (adição de papel em andamento)
  hasRoleDraft: boolean = false;
  roleDraftInfo: any = null;

  // Papéis aprovados do usuário (para filtrar opções e mostrar badges)
  approvedRoles: any[] = [];

  // Tab ativa
  activeTab: 'all' | 'assignor' | 'shareholder' | 'provider' = 'all';

  // Armazenar filtros separados por aba
  tabFilters: {
    all: any;
    assignor: any;
    shareholder: any;
    provider: any;
  } = {
    all: null,
    assignor: null,
    shareholder: null,
    provider: null,
  };

  // Controle de requisições para evitar race conditions
  private requestCounter: number = 0;

  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   *
   * Isso garante que o botão "Limpar filtros" só apareça após os filtros serem realmente aplicados,
   * não apenas quando o usuário está digitando no formulário.
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];

  // Opções de status para o filtro
  statusFilterOptions: SelectItem[] = [
    { label: 'Enviado', value: 'sent' },
    { label: 'Visualizado', value: 'viewed' },
    { label: 'Utilizado', value: 'used' },
    { label: 'Expirado', value: 'expired' },
    { label: 'Cancelado', value: 'canceled' },
  ];

  // Opções unificadas de papel + tipo de pessoa
  unifiedRoleOptions: SelectItem[] = [];

  private updateUnifiedRoleOptions() {
    const options: SelectItem[] = [];

    // Determinar o tipo de documento baseado no valor digitado
    const documentValue = this.form.controls.document.value;
    let documentType: 'CPF' | 'CNPJ' | null = null;

    if (documentValue) {
      const cleanDocument = Utils.onlyNumbers(documentValue);
      if (cleanDocument.length === 11) {
        documentType = 'CPF';
      } else if (cleanDocument.length === 14) {
        documentType = 'CNPJ';
      }
    }

    // Se houver person_type de convites usados, usar ele; senão, usar o tipo do documento digitado
    let allowedPersonTypes: string[] = [];

    if (this.documentPersonType) {
      // Se tiver person_type de convites usados, usar ele
      allowedPersonTypes =
        this.documentPersonType === 'PF' ? ['PF'] : ['PJ', 'FUNDO'];
    } else if (documentType === 'CPF') {
      // Se for CPF, mostrar apenas PF
      allowedPersonTypes = ['PF'];
    } else if (documentType === 'CNPJ') {
      // Se for CNPJ, mostrar apenas PJ e FUNDO
      allowedPersonTypes = ['PJ', 'FUNDO'];
    } else {
      // Se não tiver documento digitado ainda, mostrar todos
      allowedPersonTypes = ['PF', 'PJ', 'FUNDO'];
    }

    // Filtrar papéis já aprovados
    const approvedApplicables = this.approvedRoles.map(
      (role: any) => role.applicable
    );

    // Cedente (PF e PJ) - só mostrar se não tiver convite usado do tipo assignor e não tiver papel aprovado
    if (
      this.permissionObj.canAddAssignor &&
      !this.usedInviteTypes.has('assignor') &&
      !approvedApplicables.includes('assignor')
    ) {
      if (allowedPersonTypes.includes('PF')) {
        options.push({
          label: 'Cedente (Pessoa Física)',
          value: 'assignor-PF',
        });
      }
      if (allowedPersonTypes.includes('PJ')) {
        options.push({
          label: 'Cedente (Pessoa Jurídica)',
          value: 'assignor-PJ',
        });
      }
    }

    // Cotista (PF, PJ e Fundo) - só mostrar se não tiver convite usado do tipo shareholder e não tiver papel aprovado
    if (
      this.permissionObj.canAddShareholder &&
      !this.usedInviteTypes.has('shareholder') &&
      !approvedApplicables.includes('shareholder')
    ) {
      if (allowedPersonTypes.includes('PF')) {
        options.push({
          label: 'Cotista (Pessoa Física)',
          value: 'shareholder-PF',
        });
      }
      if (allowedPersonTypes.includes('PJ')) {
        options.push({
          label: 'Cotista (Pessoa Jurídica)',
          value: 'shareholder-PJ',
        });
      }
      if (allowedPersonTypes.includes('FUNDO')) {
        options.push({
          label: 'Cotista (Fundo de Investimento)',
          value: 'shareholder-FUNDO',
        });
      }
    }

    // Prestador de serviço (PF e PJ) - só mostrar se não tiver convite usado do tipo provider e não tiver papel aprovado
    if (
      this.permissionObj.canAddProvider &&
      !this.usedInviteTypes.has('provider') &&
      !approvedApplicables.includes('provider')
    ) {
      if (allowedPersonTypes.includes('PF')) {
        options.push({
          label: 'Prestador de Serviço (Pessoa Física)',
          value: 'provider-PF',
        });
      }
      if (allowedPersonTypes.includes('PJ')) {
        options.push({
          label: 'Prestador de Serviço (Pessoa Jurídica)',
          value: 'provider-PJ',
        });
      }
    }

    this.unifiedRoleOptions = options;
  }

  // Método auxiliar para extrair papel e tipo da opção unificada
  private parseUnifiedRole(
    unifiedValue: string
  ): { role: string; personType: string } | null {
    if (!unifiedValue) return null;
    const [role, personType] = unifiedValue.split('-');
    return { role, personType };
  }

  // Getter para obter o personType atual
  get currentPersonType(): string | null {
    const unifiedValue = this.form.controls.unified_role.value;
    if (!unifiedValue) return null;
    const parsed = this.parseUnifiedRole(unifiedValue);
    return parsed ? parsed.personType : null;
  }

  // Getters para labels dinâmicos
  get nameLabel(): string {
    return this.currentPersonType === 'PF' ? 'Nome' : 'Razão Social';
  }

  get documentLabel(): string {
    return this.currentPersonType === 'PF' ? 'CPF' : 'CNPJ';
  }

  get nameHelpText(): string {
    return this.currentPersonType === 'PF'
      ? 'Inserir nome completo'
      : 'Inserir razão social';
  }

  agentOptions: SelectItem[] = [];
  fundOptions: SelectItem[] = [];

  invitesStatistics = {
    sendedInvites: 0,
    usedInvites: 0,
    expiredInvites: 0,
  };

  // Valores para os cards de summary
  summaryStatistics = {
    total: 0,
    pending: 0,
    sent: 0,
    viewed: 0,
    used: 0,
    expired: 0,
    canceled: 0,
    shareholder: 0,
    assignor: 0,
    provider: 0,
  };
  loadingSummary = true;

  showCancelModal = false;
  selectedId = null;
  showModal2 = false;

  inviteDetails: InviteProps = null;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private authService: AuthService,
    private storageService: StorageService,
    private toast: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  // Validador customizado para nome de pessoa física (sem números e sem pontos)
  static personNameValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    if (!control.value) {
      return null; // Se está vazio, deixa o required validator tratar
    }

    const v: string = String(control.value).trim();

    // Verifica se contém números
    if (/\d/.test(v)) {
      return { fullNameNumbers: true };
    }

    // Verifica se contém pontos
    if (/\./.test(v)) {
      return { fullNameDots: true };
    }

    // Aplica as outras validações do fullName
    const fullNameError = UtilsValidators.fullName(control);
    if (fullNameError) {
      return fullNameError;
    }

    return null;
  }

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_invites']);

    this.setPermissions();

    // Inicializar opções unificadas de papel
    this.updateUnifiedRoleOptions();

    // getData já faz ambas as requisições (listagem e summary) em Promise.all
    // então não precisamos chamar getSummary separadamente
    this.getData();
    this.getFunds();
    this.getAgents();

    // Quando o papel unificado mudar
    this.form.controls.unified_role.valueChanges.subscribe((unifiedValue) => {
      if (!unifiedValue) {
        // Sem papel selecionado: valores padrão
        // Nome sem validação fullName (será adicionada quando papel for selecionado se for PF)
        this.form.controls.name.setValidators([Validators.required]);
        this.form.controls.agent.setValidators([]);
        this.form.controls.fund.setValidators([]);

        // Limpar apenas agent e fund quando o papel for removido
        // Não limpar nome e email - a validação é feita pelo documento
        this.form.controls.agent.setValue(null, { emitEvent: false });
        this.form.controls.fund.setValue(null, { emitEvent: false });

        this.form.controls.name.updateValueAndValidity();

        this.form.controls.agent.updateValueAndValidity();
        this.form.controls.fund.updateValueAndValidity();
        return;
      }

      const parsed = this.parseUnifiedRole(unifiedValue);
      if (!parsed) return;

      const { role, personType } = parsed;

      // Verificar se o documento já foi preenchido e validar compatibilidade
      const documentValue = this.form.controls.document.value;
      if (documentValue) {
        const cleanDocument = Utils.onlyNumbers(documentValue);
        const isCPF = cleanDocument.length === 11;
        const isCNPJ = cleanDocument.length === 14;

        // Verificar compatibilidade entre documento e tipo de pessoa selecionado
        if (personType === 'PF' && !isCPF) {
          this.documentInviteError =
            'O papel selecionado requer CPF (Pessoa Física), mas foi informado um CNPJ';
          this.showOtherFields = false;
          return;
        } else if ((personType === 'PJ' || personType === 'FUNDO') && !isCNPJ) {
          this.documentInviteError =
            'O papel selecionado requer CNPJ (Pessoa Jurídica), mas foi informado um CPF';
          this.showOtherFields = false;
          return;
        }
      }

      // Atualizar validação do nome baseado no tipo de pessoa
      // PF: usar UtilsValidators.fullName, PJ/FUNDO: apenas required
      if (personType === 'PF') {
        this.form.controls.name.setValidators([
          Validators.required,
          UtilsValidators.fullName,
        ]);
      } else {
        // PJ ou FUNDO: apenas required (Razão Social pode ter vários formatos)
        this.form.controls.name.setValidators([Validators.required]);
      }

      this.form.controls.name.updateValueAndValidity();

      // NÃO limpar campos quando o papel mudar - a validação é feita pelo documento
      // Apenas atualizar validações e limpar agent e fund se necessário
      this.form.controls.agent.setValue(null, { emitEvent: false });
      this.form.controls.fund.setValue(null, { emitEvent: false });

      // Configurar validação de agent e fund baseado no papel
      if (role === 'provider') {
        this.form.controls.agent.setValidators([Validators.required]);
        this.form.controls.fund.setValidators([]); // Fundo não obrigatório para provider
      } else {
        this.form.controls.agent.setValidators([]);
        this.form.controls.fund.setValidators([Validators.required]);
      }

      // Se for shareholder-FUNDO, marcar shareholder_fi automaticamente
      if (unifiedValue === 'shareholder-FUNDO') {
        this.form.controls.shareholder_fi.setValue(true);
      } else {
        this.form.controls.shareholder_fi.setValue(false);
      }

      this.form.controls.agent.updateValueAndValidity();
      this.form.controls.fund.updateValueAndValidity();
    });

    this.form.controls.shareholder_fi.valueChanges.subscribe((value) => {
      const unifiedValue = this.form.controls.unified_role.value;
      if (!unifiedValue) return;

      const parsed = this.parseUnifiedRole(unifiedValue);
      if (!parsed) return;

      // Se marcar como fundo de investimento, garantir que o documento seja CNPJ
      if (value) {
        const documentValue = this.form.controls.document.value;
        if (documentValue) {
          const cleanDocument = Utils.onlyNumbers(documentValue);
          if (cleanDocument.length === 11) {
            // Se for CPF, mostrar erro
            this.documentInviteError =
              'Fundo de investimento requer CNPJ, mas foi informado um CPF';
            this.showOtherFields = false;
            return;
          }
        }
        // Se for CNPJ, atualizar máscara
        this.documentMask = '00.000.000/0000-00';
      } else {
        // Se desmarcar, voltar a validação baseada no tipo de pessoa do papel
        if (parsed.personType === 'PF') {
          this.documentMask = '000.000.000-00';
          this.form.controls.name.setValidators([
            Validators.required,
            UtilsValidators.fullName,
          ]);
        } else if (
          parsed.personType === 'PJ' ||
          parsed.personType === 'FUNDO'
        ) {
          this.documentMask = '00.000.000/0000-00';
          this.form.controls.name.setValidators([Validators.required]);
        }
      }
      this.form.controls.name.updateValueAndValidity();
    });

    // Listener para atualizar máscara quando o documento for digitado (sem verificação automática)
    this.form.controls.document.valueChanges.subscribe((documentValue) => {
      // Se estiver editando (reenviando convite), NÃO resetar os campos
      if (this.editing && this.inviteId) {
        return;
      }

      // Limpar estados quando o documento mudar
      this.documentInviteError = null;
      this.showOtherFields = false;
      this.documentVerified = false; // Resetar verificação
      this.usedInviteTypes.clear(); // Limpar tipos usados
      this.documentPersonType = null; // Limpar person_type
      this.existingInviteData = null; // Limpar dados do convite existente

      // Resetar validação de documento
      this.checkingDocument = false;
      this.documentExists = null;
      this.availablePapers = [];
      this.currentPapers = [];
      this.userName = '';
      this.isCollaborator = false;
      this.collaboratorInfo = null;
      this.collaboratorMessage = '';
      this.hasPendingApproval = false;
      this.pendingApprovalRole = '';
      this.hasInviteSent = false;
      this.inviteStatus = '';
      this.invitedData = null;
      this.hasRoleDraft = false;
      this.roleDraftInfo = null;
      this.approvedRoles = [];

      // Resetar campos do formulário quando o documento mudar
      // (exceto o próprio campo de documento)
      this.form.controls.name.setValue(null, { emitEvent: false });
      this.form.controls.email.setValue(null, { emitEvent: false });
      this.form.controls.unified_role.setValue(null, { emitEvent: false });
      this.form.controls.agent.setValue(null, { emitEvent: false });
      this.form.controls.fund.setValue(null, { emitEvent: false });

      // Se o documento estiver vazio, restaurar opções originais
      if (!documentValue) {
        this.updateUnifiedRoleOptions();
        return;
      }

      // Remover formatação do documento
      const cleanDocument = Utils.onlyNumbers(documentValue);

      // Verificar se o documento tem tamanho válido (CPF: 11, CNPJ: 14)
      if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
        this.updateUnifiedRoleOptions(); // Atualizar opções mesmo se não estiver completo
        return;
      }

      // Atualizar máscara baseado no tamanho do documento
      if (cleanDocument.length === 11) {
        this.documentMask = '000.000.000-00';
      } else if (cleanDocument.length === 14) {
        this.documentMask = '00.000.000/0000-00';
      }

      // Atualizar opções de papel baseado no tipo de documento
      this.updateUnifiedRoleOptions();
    });
  }

  async toggleSortButton() {
    this.sortButtonExpanded = !this.sortButtonExpanded;
    // Recarrega os dados com a nova ordenação da API
    await this.getData();
  }

  // Carregar os filtros da aba especificada
  loadTabFilters(tab: 'all' | 'assignor' | 'shareholder' | 'provider') {
    const savedFilters = this.tabFilters[tab];

    if (savedFilters) {
      // Restaurar os filtros no formulário
      this.filterForm.patchValue({
        ...savedFilters,
      });

      // Atualizar estado de filtros
      this.hasFilters = true;
      this.generateFilterTags(this.filterForm.value);
    } else {
      // Se não há filtros salvos, garantir que está limpo
      this.hasFilters = false;
      this.appliedFilters = [];
    }
  }

  /**
   * Retorna o parâmetro tab para a API baseado na tab ativa
   */
  getTabParam(): string | null {
    if (this.activeTab === 'all') {
      return null;
    }
    return this.activeTab;
  }

  /**
   * Handler para mudança de tab
   */
  async onTabChange(tab: 'all' | 'assignor' | 'shareholder' | 'provider') {
    // Limpar os filtros da aba atual antes de mudar (não salvar)
    this.tabFilters[this.activeTab] = null;

    // Limpar o formulário antes de mudar de aba
    this.filterForm.reset();

    // Limpar estado de filtros
    this.hasFilters = false;
    this.appliedFilters = [];

    // Ativar ambos os loadings imediatamente
    this.loading = true;
    this.loadingSummary = true;

    // Incrementar contador de requisições
    this.requestCounter++;
    const currentRequestId = this.requestCounter;

    // Mudar para a nova aba
    this.activeTab = tab;

    // Carregar os filtros da nova aba (se existirem)
    this.loadTabFilters(tab);

    // getData já faz ambas as requisições (listagem e summary) em Promise.all
    // então não precisamos chamar getSummary separadamente
    await this.getData(currentRequestId);
  }

  async refreshData() {
    // getData já faz ambas as requisições (listagem e summary) em Promise.all
    // então não precisamos chamar getSummary separadamente
    await this.getData();
  }

  getSortButtonLabel() {
    return this.sortButtonExpanded ? 'Mais antigos' : 'Mais recentes';
  }

  async getData(requestId?: number) {
    // Se não foi passado requestId, usar o contador atual (para compatibilidade com outras chamadas)
    const currentRequestId = requestId || ++this.requestCounter;

    this.loading = true;
    this.loadingSummary = true;

    // Carregar os filtros da aba atual (se existirem)
    this.loadTabFilters(this.activeTab);

    // Construir filtros a partir do formulário
    const filters: any = {};
    const values = this.filterForm.value;
    Object.entries(values).forEach(([key, value]: any) => {
      if (value !== null && value !== '') {
        filters[key] = value;
      }
    });

    // ✅ Adicionar parâmetro tab se não for 'all'
    const tabParam = this.getTabParam();
    if (tabParam) {
      filters.tab = tabParam;
    }

    // Construir parâmetros para a API de listagem
    const listParams: any = {
      page: 1,
      limit: 10,
      type: 'list',
      order_by: this.sortButtonExpanded ? '-created_at' : 'created_at',
      ...filters,
    };

    // Construir parâmetros para a API de summary (mesmos filtros, mas com tratamento de documento)
    const summaryFilters: any = {};
    Object.entries(values).forEach(([key, value]: any) => {
      if (value !== null && value !== '') {
        // Remover formatação do documento (CPF/CNPJ)
        summaryFilters[key] =
          key === 'document' ? Utils.onlyNumbers(value) : value;
      }
    });

    // Se houver filtros salvos em dataFilters, usar eles também
    if (this.dataFilters) {
      Object.assign(summaryFilters, this.dataFilters);
    }

    const summaryParams: any = {
      ...summaryFilters,
    };

    // Se não for "all", adicionar o filtro de papel
    if (this.activeTab !== 'all') {
      summaryParams.papel = this.activeTab;
    }

    // Adicionar parâmetro tab se não for 'all'
    if (tabParam) {
      summaryParams.tab = tabParam;
    }

    // ✅ Fazer requisições em paralelo
    try {
      const [response, summaryResponse] = await Promise.all([
        this.api.get({
          route: 'api/v3/registration/invite/',
          params: listParams,
          token: true,
        }),
        this.api
          .get<{
            data: {
              total: number;
              by_type: {
                shareholder: number;
                assignor: number;
                provider: number;
                generic: number;
              };
              by_status: {
                pending: number;
                sent: number;
                viewed: number;
                used: number;
                expired: number;
                canceled: number;
              };
            };
          }>({
            route: 'api/v3/registration/invite/summary/',
            token: true,
            params: summaryParams,
          })
          .catch((error) => {
            console.error('Erro ao carregar summary:', error);
            // Retornar dados padrão em caso de erro
            return {
              data: {
                total: 0,
                by_type: {
                  shareholder: 0,
                  assignor: 0,
                  provider: 0,
                  generic: 0,
                },
                by_status: {
                  pending: 0,
                  sent: 0,
                  viewed: 0,
                  used: 0,
                  expired: 0,
                  canceled: 0,
                },
              },
            };
          }),
      ]);

      // Verificar se esta é a última requisição antes de atualizar os dados
      if (currentRequestId !== this.requestCounter) {
        // Esta requisição não é a mais recente, ignorar resultado
        return;
      }

      // Atualizar dados da listagem
      this.invites = response.data ?? [];
      if (response.data) this.setInviteStatistics(response.data);
      this.count = response.total; // Usar total para paginação
      this.offset = response.offset;
      this.showing_to = response.showing_to || 0;
      this.resultsPerPage = response.results_per_page || 10;

      // Atualizar dados do summary
      this.summaryStatistics = {
        total: summaryResponse.data.total ?? 0,
        pending: summaryResponse.data.by_status?.pending ?? 0,
        sent: summaryResponse.data.by_status?.sent ?? 0,
        viewed: summaryResponse.data.by_status?.viewed ?? 0,
        used: summaryResponse.data.by_status?.used ?? 0,
        expired: summaryResponse.data.by_status?.expired ?? 0,
        canceled: summaryResponse.data.by_status?.canceled ?? 0,
        shareholder: summaryResponse.data.by_type?.shareholder ?? 0,
        assignor: summaryResponse.data.by_type?.assignor ?? 0,
        provider: summaryResponse.data.by_type?.provider ?? 0,
      };
    } catch (error) {
      console.warn(error);
      // Se esta não for a última requisição, não fazer nada
      if (currentRequestId !== this.requestCounter) {
        return;
      }
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os convites.'
      );
    } finally {
      // Só desativar loading se esta for a última requisição
      if (currentRequestId === this.requestCounter) {
        this.loading = false;
        this.loadingSummary = false;
      }
    }
  }

  async getSummary() {
    this.loadingSummary = true;
    try {
      // Construir filtros a partir do formulário (mesmo processo que getData/filterData)
      const filters: any = {};
      const values = this.filterForm.value;

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Remover formatação do documento (CPF/CNPJ)
          filters[key] = key === 'document' ? Utils.onlyNumbers(value) : value;
        }
      });

      // Se houver filtros salvos em dataFilters, usar eles também
      if (this.dataFilters) {
        Object.assign(filters, this.dataFilters);
      }

      // Montar parâmetros baseado na tab ativa
      const params: any = {
        ...filters, // Aplicar os filtros do formulário
      };

      // Se não for "all", adicionar o filtro de papel
      if (this.activeTab !== 'all') {
        params.papel = this.activeTab;
      }

      // Adicionar parâmetro tab se não for 'all' (mesmo que getData faz)
      const tabParam = this.getTabParam();
      if (tabParam) {
        params.tab = tabParam;
      }

      const { data } = await this.api.get<{
        data: {
          total: number;
          by_type: {
            shareholder: number;
            assignor: number;
            provider: number;
            generic: number;
          };
          by_status: {
            pending: number;
            sent: number;
            viewed: number;
            used: number;
            expired: number;
            canceled: number;
          };
        };
      }>({
        route: 'api/v3/registration/invite/summary/',
        token: true,
        params: params,
      });

      this.summaryStatistics = {
        total: data.total ?? 0,
        pending: data.by_status?.pending ?? 0,
        sent: data.by_status?.sent ?? 0,
        viewed: data.by_status?.viewed ?? 0,
        used: data.by_status?.used ?? 0,
        expired: data.by_status?.expired ?? 0,
        canceled: data.by_status?.canceled ?? 0,
        shareholder: data.by_type?.shareholder ?? 0,
        assignor: data.by_type?.assignor ?? 0,
        provider: data.by_type?.provider ?? 0,
      };
    } catch (error) {
      console.error(error);
      // Em caso de erro, mantém os valores padrão (0)
    } finally {
      this.loadingSummary = false;
    }
  }

  async filterData() {
    this.loading = true;
    this.loadingSummary = true;

    try {
      const values = this.filterForm.value;

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Remover formatação do documento (CPF/CNPJ)
          filters[key] = key === 'document' ? Utils.onlyNumbers(value) : value;
        }
      });

      // Adicionar parâmetro tab se não for 'all'
      const tabParam = this.getTabParam();
      if (tabParam) {
        filters.tab = tabParam;
      }

      // Construir parâmetros para a API de listagem
      const listParams = {
        page: 1,
        limit: 10,
        type: 'list',
        order_by: this.sortButtonExpanded ? '-created_at' : 'created_at',
        ...filters,
      };

      // Construir parâmetros para a API de summary (mesmos filtros)
      const summaryParams: any = {
        ...filters,
      };

      // Se não for "all", adicionar o filtro de papel
      if (this.activeTab !== 'all') {
        summaryParams.papel = this.activeTab;
      }

      // Chamar ambas as APIs em paralelo
      const [response, summaryResponse] = await Promise.all([
        this.api.get({
          route: 'api/v3/registration/invite/',
          params: listParams,
          token: true,
        }),
        this.api
          .get<{
            data: {
              total: number;
              by_type: {
                shareholder: number;
                assignor: number;
                provider: number;
                generic: number;
              };
              by_status: {
                pending: number;
                sent: number;
                viewed: number;
                used: number;
                expired: number;
                canceled: number;
              };
            };
          }>({
            route: 'api/v3/registration/invite/summary/',
            token: true,
            params: summaryParams,
          })
          .catch((error) => {
            console.error('Erro ao carregar summary:', error);
            // Retornar dados padrão em caso de erro
            return {
              data: {
                total: 0,
                by_type: {
                  shareholder: 0,
                  assignor: 0,
                  provider: 0,
                  generic: 0,
                },
                by_status: {
                  pending: 0,
                  sent: 0,
                  viewed: 0,
                  used: 0,
                  expired: 0,
                  canceled: 0,
                },
              },
            };
          }),
      ]);

      const { count, data, offset, showing_to, results_per_page } = response;

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // Salvar os filtros na aba atual
      if (this.hasFilters) {
        // Remover o parâmetro 'tab' dos filtros antes de salvar (ele é específico da aba)
        const filtersToSave = { ...filters };
        delete filtersToSave.tab;
        this.tabFilters[this.activeTab] = filtersToSave;
      } else {
        this.tabFilters[this.activeTab] = null;
      }

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      this.dataFilters = Object.keys(filters).length > 0 ? filters : null;

      this.count = count;
      this.offset = offset;
      this.showing_to = showing_to || 0;
      this.resultsPerPage = results_per_page || 10;

      this.invites = data;

      // Atualizar dados do summary
      this.summaryStatistics = {
        total: summaryResponse.data.total ?? 0,
        pending: summaryResponse.data.by_status?.pending ?? 0,
        sent: summaryResponse.data.by_status?.sent ?? 0,
        viewed: summaryResponse.data.by_status?.viewed ?? 0,
        used: summaryResponse.data.by_status?.used ?? 0,
        expired: summaryResponse.data.by_status?.expired ?? 0,
        canceled: summaryResponse.data.by_status?.canceled ?? 0,
        shareholder: summaryResponse.data.by_type?.shareholder ?? 0,
        assignor: summaryResponse.data.by_type?.assignor ?? 0,
        provider: summaryResponse.data.by_type?.provider ?? 0,
      };
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de cedente.'
      );
      // Em caso de erro, não atualiza hasFilters
    } finally {
      this.loading = false;
      this.loadingSummary = false;
    }
  }

  async getInvites(page: number = 1, firstLoad: boolean = false) {
    // Ativar ambos os loadings para manter skeletons visíveis até ambos terminarem
    if (!firstLoad) {
      this.loading = true;
    }
    this.loadingSummary = true;

    try {
      const listParams: any = {
        ...(this.dataFilters || {}),
        page,
        limit: 10,
        type: 'list',
        order_by: this.sortButtonExpanded ? '-created_at' : 'created_at',
      };

      // Adicionar parâmetro tab se não for 'all'
      const tabParam = this.getTabParam();
      if (tabParam) {
        listParams.tab = tabParam;
      }

      // Construir parâmetros para a API de summary (usando dataFilters)
      const summaryParams: any = {
        ...(this.dataFilters || {}),
      };

      // Se não for "all", adicionar o filtro de papel
      if (this.activeTab !== 'all') {
        summaryParams.papel = this.activeTab;
      }

      // Adicionar parâmetro tab se não for 'all'
      if (tabParam) {
        summaryParams.tab = tabParam;
      }

      // Chamar ambas as APIs em paralelo
      const [response, summaryResponse] = await Promise.all([
        this.api.get({
          route: 'api/v3/registration/invite/',
          params: listParams,
          token: true,
        }),
        this.api
          .get<{
            data: {
              total: number;
              by_type: {
                shareholder: number;
                assignor: number;
                provider: number;
                generic: number;
              };
              by_status: {
                pending: number;
                sent: number;
                viewed: number;
                used: number;
                expired: number;
                canceled: number;
              };
            };
          }>({
            route: 'api/v3/registration/invite/summary/',
            token: true,
            params: summaryParams,
          })
          .catch((error) => {
            console.error('Erro ao carregar summary:', error);
            // Retornar dados padrão em caso de erro
            return {
              data: {
                total: 0,
                by_type: {
                  shareholder: 0,
                  assignor: 0,
                  provider: 0,
                  generic: 0,
                },
                by_status: {
                  pending: 0,
                  sent: 0,
                  viewed: 0,
                  used: 0,
                  expired: 0,
                  canceled: 0,
                },
              },
            };
          }),
      ]);

      this.invites = response.data ?? [];
      if (response.data) this.setInviteStatistics(response.data);
      this.count = response.total; // Usar total para paginação
      this.offset = response.offset;
      this.showing_to = response.showing_to || 0;
      this.resultsPerPage = response.results_per_page || 10;

      // Atualizar dados do summary
      this.summaryStatistics = {
        total: summaryResponse.data.total ?? 0,
        pending: summaryResponse.data.by_status?.pending ?? 0,
        sent: summaryResponse.data.by_status?.sent ?? 0,
        viewed: summaryResponse.data.by_status?.viewed ?? 0,
        used: summaryResponse.data.by_status?.used ?? 0,
        expired: summaryResponse.data.by_status?.expired ?? 0,
        canceled: summaryResponse.data.by_status?.canceled ?? 0,
        shareholder: summaryResponse.data.by_type?.shareholder ?? 0,
        assignor: summaryResponse.data.by_type?.assignor ?? 0,
        provider: summaryResponse.data.by_type?.provider ?? 0,
      };
    } catch (error) {
      console.warn(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os convites.'
      );
    } finally {
      // Desativar ambos os loadings apenas quando ambas as requisições terminarem
      // (já que ambas são feitas em Promise.all, ambas terminam juntas)
      if (!firstLoad) {
        this.loading = false;
      }
      this.loadingSummary = false;
    }
  }

  get hasAnyFilterValue(): boolean {
    const values = this.filterForm.value;
    return Object.values(values).some((value: any) => {
      if (value === null || value === '') {
        return false;
      }
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
      return true;
    });
  }

  get canApplyFilters(): boolean {
    // Verifica se há algum filtro preenchido
    if (!this.hasAnyFilterValue) {
      return false;
    }

    // Verifica se o email é válido (quando preenchido)
    const emailControl = this.filterForm.controls.email;
    if (emailControl.value && emailControl.invalid) {
      return false;
    }

    return true;
  }

  async resetForm() {
    // Ativar ambos os loadings
    this.loading = true;
    this.loadingSummary = true;
    this.hasFilters = false;
    this.appliedFilters = []; // ✅ Limpar tags
    this.filterForm.reset();
    this.dataFilters = null;

    // Limpar os filtros da aba atual
    this.tabFilters[this.activeTab] = null;

    this.showFilterModal = false; // Fechar o modal após limpar filtros
    this.cdr.detectChanges(); // Forçar detecção de mudanças

    // Resetar para a primeira página e recarregar dados
    // getInvites já gerencia ambos os loadings, então não precisa desativar aqui
    await this.getInvites(1, false);
  }

  changePageData(page: number) {
    // Ativar ambos os loadings
    this.loading = true;
    this.loadingSummary = true;
    this.getInvites(page);
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Carregar os filtros da aba atual no formulário
      const savedFilters = this.tabFilters[this.activeTab];

      if (savedFilters) {
        this.filterForm.patchValue({
          ...savedFilters,
        });

        this.hasFilters = true;
        // Gera as tags dos filtros salvos
        this.generateFilterTags(this.filterForm.value);
      } else {
        // Se não há filtros salvos, garantir que o formulário está limpo
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  closeModal() {
    this.newInvite = false;
    this.showFilterModal = false;
    this.inviteDetails = null;
    this.editing = false;
    this.inviteId = null;
    this.form.reset();
    this.documentMask = 'CPF_CNPJ'; // Resetar máscara para padrão
    this.documentInviteError = null; // Limpar erro de convite
    this.checkingInvites = false; // Limpar flag de verificação
    this.showOtherFields = false; // Resetar flag de exibição dos outros campos
    this.documentVerified = false; // Resetar verificação
    this.usedInviteTypes.clear(); // Limpar tipos de convites usados
    this.documentPersonType = null; // Limpar person_type
    this.existingInviteData = null; // Limpar dados do convite existente

    // Resetar validação de documento (similar ao dashboard-user-list)
    this.checkingDocument = false;
    this.documentExists = null;
    this.availablePapers = [];
    this.currentPapers = [];
    this.userName = '';
    this.isCollaborator = false;
    this.collaboratorInfo = null;
    this.collaboratorMessage = '';
    this.hasPendingApproval = false;
    this.pendingApprovalRole = '';
    this.hasInviteSent = false;
    this.inviteStatus = '';
    this.hasRoleDraft = false;
    this.roleDraftInfo = null;
    this.approvedRoles = [];

    // Resetar validação do documento para aceitar CPF ou CNPJ
    this.form.controls.document.setValidators([
      Validators.required,
      Utils.validateCpfCnpj,
    ]);
    this.form.controls.document.updateValueAndValidity();

    // Restaurar opções de papel originais
    this.updateUnifiedRoleOptions();
  }

  getDisabled() {
    // Verificar se o papel está selecionado
    const hasRoleSelected = !!this.form.controls.unified_role.value;

    const disabled =
      this.form.invalid ||
      !hasRoleSelected ||
      this.loading ||
      this.sendingInvite ||
      !!this.documentInviteError ||
      this.hasRoleDraft;

    return disabled;
  }

  /**
   * Verifica se o botão de verificar deve estar habilitado
   */
  canVerifyDocument(): boolean {
    const documentValue = this.form.controls.document.value;
    if (!documentValue) {
      return false;
    }

    const cleanDocument = Utils.onlyNumbers(documentValue);

    // Verificar se o documento tem tamanho válido (CPF: 11, CNPJ: 14)
    if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
      return false;
    }

    // Verificar condições de bloqueio primeiro
    if (
      this.checkingDocument ||
      this.documentExists ||
      this.hasPendingApproval ||
      this.hasInviteSent ||
      this.hasRoleDraft
    ) {
      return false;
    }

    // Validar CPF ou CNPJ
    if (cleanDocument.length === 11) {
      const cpfValid = UtilsValidators.cpf({
        value: documentValue,
      } as AbstractControl);
      return !cpfValid; // Retorna true se CPF for válido
    } else if (cleanDocument.length === 14) {
      const cnpjValid = UtilsValidators.cnpj({
        value: documentValue,
      } as AbstractControl);
      return !cnpjValid; // Retorna true se CNPJ for válido
    }

    return false;
  }

  /**
   * Método para verificar o documento manualmente
   */
  async verifyDocument() {
    const documentValue = this.form.controls.document.value;
    if (!documentValue) {
      return;
    }

    const documentClean = documentValue
      ? documentValue.replace(/[^\d]+/g, '')
      : '';

    // Resetar estado ao mudar documento
    this.documentExists = null;
    this.availablePapers = [];
    this.currentPapers = [];
    this.userName = '';
    this.isCollaborator = false;
    this.collaboratorInfo = null;
    this.collaboratorMessage = '';
    this.hasPendingApproval = false;
    this.pendingApprovalRole = '';
    this.hasInviteSent = false;
    this.inviteStatus = '';
    this.hasRoleDraft = false;
    this.roleDraftInfo = null;
    this.approvedRoles = [];

    // Limpar erros anteriores
    this.form.controls.document.setErrors(null);

    // Verificar se o documento tem tamanho válido (CPF 11 dígitos ou CNPJ 14 dígitos)
    const isValidLength =
      documentClean.length === 11 || documentClean.length === 14;

    if (!isValidLength) {
      // Se não tem tamanho válido mas tem algum valor, limpar erros
      if (documentClean.length > 0 && documentClean.length < 11) {
        this.form.controls.document.setErrors(null);
      }
      return;
    }

    // Validar CPF ou CNPJ antes de chamar a API
    let isValid = false;
    let errorKey = '';
    if (documentClean.length === 11) {
      isValid = BrValidators.cpf(documentClean);
      errorKey = 'cpf';
    } else if (documentClean.length === 14) {
      isValid = BrValidators.cnpj(documentClean);
      errorKey = 'cnpj';
    }

    if (!isValid) {
      // Marcar campo como inválido
      this.form.controls.document.setErrors({ [errorKey]: true });
      this.form.controls.document.markAsTouched();
      return;
    }

    // Limpar erros antes de chamar API (documento válido)
    this.form.controls.document.setErrors(null);

    this.checkingDocument = true;

    try {
      const res = await this.api.post({
        route: 'validate-user-paper-exists/',
        body: { document: documentClean },
        token: true,
      });

      this.checkingDocument = false;

      // Verificar se há papéis com status diferente de "Aprovado"
      // current_role pode estar no nível raiz ou dentro de res.data
      this.hasPendingApproval = false;
      this.pendingApprovalRole = '';
      this.approvedRoles = [];

      const currentRoleRaw =
        res.current_role || (res.data && res.data.current_role);

      // Filtrar itens com applicable === 'representative' - ignorar esses itens
      const currentRole =
        currentRoleRaw && Array.isArray(currentRoleRaw)
          ? currentRoleRaw.filter(
              (role: any) => role.applicable !== 'representative'
            )
          : currentRoleRaw;

      // Se após filtrar representative, o array ficar vazio, tratar como se não houvesse current_role
      const hasValidRoles =
        currentRole && Array.isArray(currentRole) && currentRole.length > 0;

      if (hasValidRoles) {
        // Separar papéis aprovados e pendentes
        const pendingRole = currentRole.find(
          (role: any) => role.status && role.status !== 'Aprovado'
        );

        if (pendingRole) {
          this.hasPendingApproval = true;
          this.pendingApprovalRole = pendingRole.applicable_display || '';
        }

        // Armazenar papéis aprovados para filtrar opções e mostrar badges
        this.approvedRoles = currentRole.filter(
          (role: any) => role.status === 'Aprovado'
        );
      }

      // Verificar se há convite já enviado com status sent ou viewed
      // Se for used, não bloqueia (permite outras verificações)
      this.hasInviteSent = false;
      this.inviteStatus = '';
      this.invitedData = null;

      const invited = res.invited || (res.data && res.data.invited);
      if (invited && invited.status) {
        const inviteStatus = invited.status;
        if (inviteStatus === 'sent' || inviteStatus === 'viewed') {
          this.hasInviteSent = true;
          this.inviteStatus = inviteStatus;
          this.invitedData = invited; // Armazenar dados completos do convite
        }
      }

      // Verificar se há role_draft (adição de papel em andamento)
      // Se role_draft existir (não for null/undefined), significa que há adição de papel em andamento
      // role_draft pode estar no nível raiz ou dentro de res.data
      const roleDraft = res.role_draft || (res.data && res.data.role_draft);

      this.hasRoleDraft = false;
      this.roleDraftInfo = null;

      if (roleDraft !== null && roleDraft !== undefined) {
        // Sempre travar quando houver role_draft, independentemente de estar aprovado
        // Se há role_draft, significa que há uma adição de papel em andamento
        this.hasRoleDraft = true;
        this.roleDraftInfo = roleDraft;
        // Quando há role_draft, bloquear o cadastro
        this.documentExists = true;
        // Definir userName se disponível
        if (res.name || (res.data && res.data.name)) {
          this.userName = res.name || res.data.name;
        }
      }

      // Verificar se é um colaborador
      if (res.is_collaborator && res.collaborator) {
        this.isCollaborator = true;
        this.collaboratorInfo = res.collaborator;
        this.collaboratorMessage = res.message || '';
        this.documentExists = true; // Bloquear cadastro

        // Se houver aprovação pendente, também definir userName se disponível
        if (res.data && res.data.name) {
          this.userName = res.data.name;
        } else if (res.name) {
          // Capturar nome diretamente de res.name se disponível
          this.userName = res.name;
        }
      } else {
        this.isCollaborator = false;
        this.collaboratorInfo = null;
        this.collaboratorMessage = '';
        // Se só tinha representative (hasValidRoles = false), tratar como se não houvesse cadastro
        // Caso contrário, usar res.exists normalmente
        this.documentExists = hasValidRoles && res.exists === true;

        if (this.documentExists && res.data) {
          this.userName = res.data.name || res.name || '';
          this.availablePapers = res.data.papers || [];
          this.currentPapers = res.data.current_papers || [];
        } else if (this.documentExists && res.name) {
          this.userName = res.name;
        } else if (res.name && this.approvedRoles.length > 0) {
          // Se não documentExists mas tem papéis aprovados, definir userName
          this.userName = res.name;
        } else if (res.name) {
          // Se houver nome na resposta, definir userName mesmo sem documentExists
          this.userName = res.name;
        }
      }

      // Se houver aprovação pendente, convite já enviado ou role_draft, bloquear cadastro
      if (this.hasPendingApproval || this.hasInviteSent || this.hasRoleDraft) {
        this.documentExists = true;
        // Se houver convite enviado mas não houver userName, tentar pegar da mensagem ou definir como vazio
        if (this.hasInviteSent && !this.userName && res.message) {
          // A mensagem pode conter informações úteis, mas não vamos definir userName aqui
        }
        // Se houver role_draft mas não houver userName, pegar do res.name
        if (this.hasRoleDraft && !this.userName && res.name) {
          this.userName = res.name;
        }
      }

      // Se passou em todas as validações (não é colaborador, não tem aprovação pendente, não tem convite enviado, não tem role_draft)
      // e o documento foi verificado, mostrar os campos para preenchimento
      // Isso acontece quando exists = false OU quando exists = true mas tem papéis aprovados (pode adicionar novo papel)
      // OU quando exists = true mas só tinha representative (hasValidRoles = false) - tratar como novo cadastro
      if (
        !this.checkingDocument &&
        !this.isCollaborator &&
        !this.hasPendingApproval &&
        !this.hasInviteSent &&
        !this.hasRoleDraft
      ) {
        if (
          res.exists === false ||
          (res.exists === true && this.approvedRoles.length > 0) ||
          (res.exists === true && !hasValidRoles) // Se só tinha representative, liberar como novo cadastro
        ) {
          this.documentVerified = true;
          this.showOtherFields = true;
          // Atualizar opções de papel para filtrar os já aprovados
          this.updateUnifiedRoleOptions();

          // Preencher o campo name se houver nome disponível
          if (this.userName) {
            this.form.controls.name.setValue(this.userName);
          }

          // Preencher o campo email se houver email disponível na resposta
          const email = res.email || (res.data && res.data.email);
          if (email) {
            this.form.controls.email.setValue(email);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao validar documento:', error);
      this.checkingDocument = false;
      this.documentExists = null;
      this.isCollaborator = false;
      this.collaboratorInfo = null;
      this.collaboratorMessage = '';
      this.hasPendingApproval = false;
      this.pendingApprovalRole = '';
      this.hasInviteSent = false;
      this.inviteStatus = '';
      this.invitedData = null;
      this.hasRoleDraft = false;
      this.roleDraftInfo = null;
      this.approvedRoles = [];
    }

    // Função original comentada:
    // const cleanDocument = Utils.onlyNumbers(documentValue);
    // // Verificar se o documento tem tamanho válido
    // if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
    //   return;
    // }
    // // Fazer busca de convites
    // await this.checkExistingInvites(cleanDocument);
  }

  /**
   * Retorna o tipo do documento (CPF ou CNPJ) baseado no valor atual
   */
  getDocumentType(): string {
    const documentValue = this.form.controls.document.value;
    if (!documentValue) {
      return 'CPF / CNPJ';
    }
    const cleanDocument = Utils.onlyNumbers(documentValue);
    return cleanDocument.length === 11 ? 'CPF' : 'CNPJ';
  }

  /**
   * Métodos auxiliares para exibir informações do documento verificado
   */
  getCurrentPapersText(): string {
    if (!this.currentPapers || this.currentPapers.length === 0) {
      return '';
    }
    return this.currentPapers.map((p) => p.description).join(', ');
  }

  getCollaboratorCompanies(): string {
    if (
      !this.collaboratorInfo ||
      !this.collaboratorInfo.companies ||
      this.collaboratorInfo.companies.length === 0
    ) {
      return '';
    }
    return this.collaboratorInfo.companies
      .map((c) => c.brand_name || c.corporate_name)
      .join(', ');
  }

  getCollaboratorDepartments(): string {
    if (
      !this.collaboratorInfo ||
      !this.collaboratorInfo.departments ||
      this.collaboratorInfo.departments.length === 0
    ) {
      return '';
    }
    return this.collaboratorInfo.departments.map((d) => d.name).join(', ');
  }

  getCollaboratorType(): string {
    if (!this.collaboratorInfo || !this.collaboratorInfo.collaborator_type) {
      return '';
    }
    const type = this.collaboratorInfo.collaborator_type;
    // Se for master ou owner, retorna 'master', senão retorna 'comum'
    if (type === 'master' || type === 'owner') {
      return 'master';
    }
    return 'comum';
  }

  getCollaboratorDocument(): string {
    if (!this.collaboratorInfo || !this.collaboratorInfo.document) {
      return '';
    }
    const doc = this.collaboratorInfo.document;
    // Formatar CPF (11 dígitos) ou CNPJ (14 dígitos)
    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (doc.length === 14) {
      return doc.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      );
    }
    return doc;
  }

  /**
   * Retorna o texto dos papéis aprovados para exibição na mensagem
   */
  getApprovedRolesText(): string {
    if (!this.approvedRoles || this.approvedRoles.length === 0) {
      return '';
    }
    const roleLabels = this.approvedRoles.map((role: any) => {
      const typeMap: { [key: string]: string } = {
        assignor: 'cedente',
        shareholder: 'cotista',
        provider: 'prestador de serviço',
      };
      return (
        typeMap[role.applicable] || role.applicable_display || role.applicable
      );
    });

    if (roleLabels.length === 1) {
      return roleLabels[0];
    } else if (roleLabels.length === 2) {
      return roleLabels.join(' e ');
    } else {
      const last = roleLabels.pop();
      return roleLabels.join(', ') + ' e ' + last;
    }
  }

  /**
   * Retorna o label do tipo de convite
   */
  getInviteTypeLabel(type: string): string {
    const typeMap: { [key: string]: string } = {
      assignor: 'cedente',
      shareholder: 'cotista',
      provider: 'prestador de serviço',
    };
    return typeMap[type] || type;
  }

  /**
   * Verifica se já existem convites para o documento informado
   */
  async checkExistingInvites(document: string) {
    try {
      this.checkingInvites = true;
      this.documentInviteError = null;
      this.usedInviteTypes.clear(); // Limpar tipos usados
      this.documentPersonType = null; // Limpar person_type
      this.existingInviteData = null; // Limpar dados existentes
      // Limpar o papel ao iniciar nova verificação
      this.form.controls.unified_role.setValue(null);

      // Buscar convites na API com a URL fornecida
      const response: any = await this.api.get({
        route: 'api/v3/registration/invite/all-system/',
        params: {
          document: document,
          page: 1,
          limit: 1000, // Buscar todos os convites para este documento
        },
        external: true,
        token: true,
      });

      const invites: InviteProps[] = response.data || [];

      // Se não tiver nenhum convite, pode prosseguir
      if (invites.length === 0) {
        this.checkingInvites = false;
        this.showOtherFields = true; // Mostrar outros campos se não houver convites
        this.documentVerified = true; // Marcar documento como verificado
        this.existingInviteData = null; // Garantir que não há dados existentes
        // Limpar o papel quando não houver dados existentes
        this.form.controls.unified_role.setValue(null);
        this.updateUnifiedRoleOptions(); // Atualizar opções de papel (mostra todas)
        return;
      }

      // Verificar convites usados (used: true ou status: 'used')
      const usedInvites = invites.filter(
        (invite) => invite.used === true || invite.status === 'used'
      );

      // Capturar dados do primeiro convite usado para preencher os campos
      if (usedInvites.length > 0) {
        this.existingInviteData = usedInvites[0];

        // Preencher papel automaticamente baseado no tipo e person_type
        const inviteType = this.existingInviteData.type;
        const personType =
          (this.existingInviteData as any).person_type ||
          this.documentPersonType;
        if (inviteType && personType) {
          const unifiedRole = `${inviteType}-${personType}`;
          this.form.controls.unified_role.setValue(unifiedRole);
        }

        // Preencher campos automaticamente com os dados existentes
        if (this.existingInviteData.name) {
          this.form.controls.name.setValue(this.existingInviteData.name);
        }
        if (this.existingInviteData.email) {
          this.form.controls.email.setValue(this.existingInviteData.email);
        }
        if (this.existingInviteData.fund_interest_id) {
          this.form.controls.fund.setValue(
            this.existingInviteData.fund_interest_id
          );
        }
        if (
          this.existingInviteData.agent &&
          Array.isArray(this.existingInviteData.agent)
        ) {
          // Extrair IDs dos agentes se for array de objetos
          let agentIds: number[] = [];
          if (this.existingInviteData.agent.length > 0) {
            if (
              typeof this.existingInviteData.agent[0] === 'object' &&
              'id' in this.existingInviteData.agent[0]
            ) {
              agentIds = (
                this.existingInviteData.agent as Array<{
                  id: number;
                  name: string;
                }>
              ).map((a) => a.id);
            } else if (typeof this.existingInviteData.agent[0] === 'number') {
              agentIds = this.existingInviteData.agent as number[];
            }
          }
          if (agentIds.length > 0) {
            this.form.controls.agent.setValue(agentIds);
          }
        }
      } else {
        this.existingInviteData = null;
      }

      // Identificar quais tipos de convites estão usados
      usedInvites.forEach((invite) => {
        const inviteType = invite.type; // assignor, shareholder, provider
        if (inviteType) {
          this.usedInviteTypes.add(inviteType);
        }

        // Capturar person_type do primeiro convite usado (assumindo que todos têm o mesmo person_type)
        if (!this.documentPersonType && (invite as any).person_type) {
          this.documentPersonType = (invite as any).person_type;
        }
      });

      // Verificar se todos os 3 tipos estão usados (assignor, shareholder, provider)
      const allTypesUsed =
        this.usedInviteTypes.has('assignor') &&
        this.usedInviteTypes.has('shareholder') &&
        this.usedInviteTypes.has('provider');

      // Se todos os 3 tipos estiverem usados, bloquear completamente
      if (allTypesUsed) {
        this.documentInviteError =
          'Este documento já possui convites usados para todos os tipos (Cedente, Cotista e Prestador de Serviço)';
        this.showOtherFields = false;
        this.toast.show(
          'error',
          'Todos os convites usados',
          'Este documento já possui convites usados para todos os tipos disponíveis'
        );
        this.checkingInvites = false;
        return;
      }

      // Verificar se há convites enviados ou visualizados (não usados)
      const hasSentOrViewed = invites.some(
        (invite) =>
          (invite.status === 'sent' ||
            invite.status === 'viewed' ||
            invite.viewed_at) &&
          !invite.used &&
          invite.status !== 'used'
      );

      // Se tiver convite enviado ou visualizado (não usado), bloquear
      if (hasSentOrViewed) {
        this.documentInviteError =
          'Já existe um convite enviado para este documento';
        this.showOtherFields = false;
        this.toast.show(
          'error',
          'Convite existente',
          'Já existe um convite enviado ou visualizado para este documento'
        );
        this.checkingInvites = false;
        return;
      }

      // Se tiver só cancelados, expirados ou usados, pode prosseguir (mas filtrar opções)
      this.checkingInvites = false;
      this.showOtherFields = true;
      this.documentVerified = true; // Marcar documento como verificado

      // Se não houver dados existentes, limpar o papel
      if (!this.existingInviteData) {
        this.form.controls.unified_role.setValue(null);
      }

      // Atualizar opções de papel baseado nos convites usados e person_type
      this.updateUnifiedRoleOptions();
    } catch (error) {
      console.error('Erro ao verificar convites:', error);
      this.checkingInvites = false;
      // Em caso de erro, não bloquear o usuário
      this.showOtherFields = true;
      this.updateUnifiedRoleOptions(); // Atualizar opções mesmo em caso de erro
    }
  }

  // Método auxiliar para verificar se um role específico está selecionado
  hasRole(role: string): boolean {
    const unifiedValue = this.form.controls.unified_role.value;
    if (!unifiedValue) return false;
    const parsed = this.parseUnifiedRole(unifiedValue);
    return parsed ? parsed.role === role : false;
  }

  async sendInvite() {
    // Verificar se há erro de convite antes de enviar
    if (this.documentInviteError) {
      this.toast.show('error', 'Erro', this.documentInviteError);
      return;
    }

    this.sendingInvite = true;
    try {
      // Extrair role e person_type do unified_role
      const parsed = this.parseUnifiedRole(
        this.form.controls.unified_role.value
      );
      if (!parsed) {
        this.toast.show('error', 'Erro', 'Papel inválido selecionado');
        return;
      }

      const { role, personType } = parsed;

      const formValue = { ...this.form.value };
      delete formValue.unified_role; // Remover unified_role do formValue

      // Adicionar role e person_type separados
      formValue.role = role;
      formValue.person_type = personType;

      // Verificar se é adição de papel (documento existe e tem papéis aprovados)
      const isNewPaper = this.documentExists && this.approvedRoles.length > 0;

      // Enviar convite
      await this.api.post({
        route: 'api/v3/registration/invite/',
        token: true,
        body: {
          data: {
            ...formValue,
            ...(isNewPaper && { new_paper: true }),
          },
          type: role,
        },
      });

      this.toast.show('info', 'Sucesso', 'Convite enviado com sucesso');

      // Fechar o modal imediatamente após criar o convite
      this.closeModal();

      // Chamar getData() e getSummary() ao mesmo tempo
      // getData já faz ambas as requisições (listagem e summary) em Promise.all
      await this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao enviar o convite');
    } finally {
      this.sendingInvite = false;
    }
  }

  setInviteStatistics(arr: InviteProps[] | InviteProps) {
    let usedInvites = this.invitesStatistics.usedInvites;
    let expiredInvites = this.invitesStatistics.expiredInvites;

    let auxArr: InviteProps[];

    if (Array.isArray(arr)) {
      auxArr = arr;
    } else {
      auxArr = [arr];
    }

    auxArr.forEach((item) => {
      if (item.used) {
        usedInvites++;
      } else {
        const expirationDate = this.displayExpirationDate(item.created_at);
        const today = new Date();

        const expirationDateArr = expirationDate.split('/');

        const expirationDateObj = new Date(
          parseInt(expirationDateArr[2]),
          parseInt(expirationDateArr[1]) - 1,
          parseInt(expirationDateArr[0])
        );

        if (expirationDateObj < today) {
          expiredInvites++;
        }
      }
    });

    this.invitesStatistics = {
      sendedInvites: this.invites.length,
      usedInvites: usedInvites,
      expiredInvites: expiredInvites,
    };
  }

  displayStatus(item: InviteProps) {
    // Se canceled for true, sempre retornar Cancelado
    if (item.canceled) {
      let dateTime = null;
      if ((item as any).canceled_at) {
        dateTime = this.formatDateTimeForStatus((item as any).canceled_at);
      }
      return {
        status: 'Cancelado',
        dateTime: dateTime,
      };
    }

    // Usar o campo status da API se disponível
    if (item.status) {
      const statusMap: { [key: string]: string } = {
        pending: 'Pendente',
        sent: 'Enviado',
        resend: 'Reenviado',
        viewed: 'Visualizado',
        used: 'Utilizado',
        expired: 'Expirado',
        canceled: 'Cancelado',
      };

      let label = statusMap[item.status] || item.status;
      let dateTime = null;

      // Adicionar data se houver
      if (item.used_at) {
        dateTime = this.formatDateTimeForStatus(item.used_at);
      } else if (item.viewed_at) {
        dateTime = this.formatDateTimeForStatus(item.viewed_at);
      } else if (item.sent_at) {
        dateTime = this.formatDateTimeForStatus(item.sent_at);
      }

      return {
        status: label,
        dateTime: dateTime,
      };
    }

    // Fallback para lógica antiga se não tiver status
    let label = 'Enviado';
    let dateTime = null;

    if (item.used) {
      if (item.used_at) {
        label = 'Utilizado';
        dateTime = this.formatDateTimeForStatus(item.used_at);
      } else {
        label = 'Utilizado';
      }
    } else {
      if (item.expires_at) {
        const expirationDate = this.displayExpirationDate(item.expires_at);
        const today = new Date();
        const expirationDateArr = expirationDate.split('/');
        const expirationDateObj = new Date(
          parseInt(expirationDateArr[2]),
          parseInt(expirationDateArr[1]) - 1,
          parseInt(expirationDateArr[0])
        );

        if (expirationDateObj < today) {
          label = 'Expirado';
        }
      }
    }

    return {
      status: label,
      dateTime: dateTime,
    };
  }

  // Método para obter a classe CSS do badge de status
  getStatusBadgeClass(item: InviteProps): string {
    // Se canceled for true, sempre retornar classe de cancelado
    if (item.canceled) {
      return 'status-badge-canceled';
    }

    const status = item.status || '';

    switch (status) {
      case 'pending':
        return 'status-badge-pending';
      case 'sent':
        return 'status-badge-sent';
      case 'resend':
        return 'status-badge-resend';
      case 'viewed':
        return 'status-badge-viewed';
      case 'used':
        return 'status-badge-used';
      case 'expired':
        return 'status-badge-expired';
      case 'canceled':
        return 'status-badge-canceled';
      default:
        // Fallback para lógica antiga
        if (item.used) {
          return 'status-badge-used';
        } else if (item.expires_at) {
          const expirationDate = this.displayExpirationDate(item.expires_at);
          const today = new Date();
          const expirationDateArr = expirationDate.split('/');
          const expirationDateObj = new Date(
            parseInt(expirationDateArr[2]),
            parseInt(expirationDateArr[1]) - 1,
            parseInt(expirationDateArr[0])
          );
          if (expirationDateObj < today) {
            return 'status-badge-expired';
          }
        }
        return 'status-badge-sent';
    }
  }

  formatDateTimeForStatus(dateStr: string) {
    if (!dateStr) return null;

    const date = dateStr.split('T')[0].split('-');
    const hours = dateStr.split('T')[1]?.split('.')[0].split(':') || [
      '00',
      '00',
    ];

    const day = String(parseInt(date[2])).padStart(2, '0');
    const month = String(parseInt(date[1])).padStart(2, '0');
    const year = date[0];

    return {
      date: `${day}/${month}/${year}`,
      time: `às ${hours[0]}:${hours[1]}`,
    };
  }

  /**
   * Retorna a data/hora do status ocorrido baseado no status atual
   * - pending: "-"
   * - sent: sent_at
   * - viewed: viewed_at
   * - used: used_at
   * - expired: expires_at (quando expirou)
   * - canceled: canceled_at (quando foi cancelado)
   */
  getStatusDateTime(item: InviteProps): { date: string; time: string } {
    // Se canceled for true, sempre retornar a data de cancelamento
    if (item.canceled) {
      if ((item as any).canceled_at) {
        const dateTime = this.formatDateTimeForStatus(
          (item as any).canceled_at
        );
        return dateTime || { date: '-', time: '' };
      }
      return { date: '-', time: '' };
    }

    const status = item.status || '';

    // Verificar se está expirado (mesmo sem status expired)
    const expired = this.isExpired(item);

    // Se for pendente, retorna "-"
    if (status === 'pending' || (!status && !item.used && !expired)) {
      return { date: '-', time: '' };
    }

    let dateTime: { date: string; time: string } | null = null;

    // Prioridade: usado > expirado > visualizado > enviado
    if (status === 'used' && item.used_at) {
      dateTime = this.formatDateTimeForStatus(item.used_at);
    } else if (item.used && item.used_at) {
      // Fallback para lógica antiga
      dateTime = this.formatDateTimeForStatus(item.used_at);
    } else if (status === 'expired' && item.expires_at) {
      dateTime = this.formatDateTimeForStatus(item.expires_at);
    } else if (expired && item.expires_at) {
      dateTime = this.formatDateTimeForStatus(item.expires_at);
    } else if (status === 'viewed' && item.viewed_at) {
      dateTime = this.formatDateTimeForStatus(item.viewed_at);
    } else if (item.viewed_at) {
      dateTime = this.formatDateTimeForStatus(item.viewed_at);
    } else if (status === 'sent' && item.sent_at) {
      dateTime = this.formatDateTimeForStatus(item.sent_at);
    } else if (item.sent_at) {
      dateTime = this.formatDateTimeForStatus(item.sent_at);
    }

    if (dateTime) {
      return dateTime;
    }

    return { date: '-', time: '' };
  }

  /**
   * Verifica se o convite está expirado
   */
  private isExpired(item: InviteProps): boolean {
    if (!item.expires_at) return false;

    const expirationDate = this.displayExpirationDate(item.expires_at);
    const today = new Date();
    const expirationDateArr = expirationDate.split('/');
    const expirationDateObj = new Date(
      parseInt(expirationDateArr[2]),
      parseInt(expirationDateArr[1]) - 1,
      parseInt(expirationDateArr[0])
    );

    return expirationDateObj < today;
  }

  displaySendDate(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    const date = dateStr.split('T')[0].split('-');
    const hours = dateStr.split('T')[1]?.split('.')[0].split(':') || [
      '00',
      '00',
    ];

    const day = String(parseInt(date[2])).padStart(2, '0');
    const month = String(parseInt(date[1])).padStart(2, '0');
    const year = date[0];

    return {
      date: `${day}/${month}/${year}`,
      time: `às ${hours[0]}:${hours[1]}`,
    };
  }

  displayExpirationDate(dateStr: string) {
    if (!dateStr) return '-';

    const date = dateStr.split('T')[0].split('-');
    const newDate = new Date(
      parseInt(date[0]),
      parseInt(date[1]) - 1,
      parseInt(date[2])
    );

    // Formatar com zero à esquerda quando necessário
    const day = String(newDate.getDate()).padStart(2, '0');
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const year = newDate.getFullYear();

    return `${day}/${month}/${year}`;
  }

  displayAgents(
    agent:
      | Array<{ id: number; name: string }>
      | number[]
      | number
      | string
      | undefined
  ) {
    if (!agent) return '';

    // Se for array de objetos com id e name (novo formato da API v3)
    if (Array.isArray(agent) && agent.length > 0) {
      // Verificar se é array de objetos
      if (
        typeof agent[0] === 'object' &&
        agent[0] !== null &&
        'name' in agent[0]
      ) {
        return (agent as Array<{ id: number; name: string }>)
          .map((a) => a.name)
          .join(', ');
      }
      // Se for array de números (formato antigo - não suportado mais)
      if (typeof agent[0] === 'number') {
        return ''; // Formato antigo não suportado mais
      }
    }

    // Se for número único (formato antigo - não suportado mais)
    if (typeof agent === 'number') {
      return ''; // Formato antigo não suportado mais
    }

    // Se for string (role), retornar vazio pois role não é mais usado para exibir agentes
    if (typeof agent === 'string') {
      return '';
    }

    return '';
  }

  toggleModal() {
    this.newInvite = !this.newInvite;

    if (this.newInvite) {
      if (this.fundOptions.length === 0) {
        this.getFunds();
      }
      if (this.agentOptions.length === 0) {
        this.getAgents();
      }
    }
  }

  async getFunds() {
    try {
      const res = await this.api.get({
        route: 'api/v2/approvals/funds-dropdown/active/',
        token: true,
      });

      // A API já retorna no formato correto com label e value
      this.fundOptions = res.data || [];
    } catch (error) {
      this.toast.show('error', 'Erro!', 'Erro ao carregar fundos de interesse');
    }
  }

  async getAgents() {
    try {
      const res = await this.api.get({
        route: 'api/v2/registration/agent/',
        token: true,
      });

      // Mapear o retorno da API para o formato SelectItem
      this.agentOptions = (res.data || res || []).map((item: any) => {
        return {
          label: item.name,
          value: item.id,
        };
      });
    } catch (error) {
      this.toast.show('error', 'Erro!', 'Erro ao carregar agentes');
    }
  }

  getInitials(item: InviteProps) {
    let name = item.name ?? null;

    return name ? Utils.getInitials(name) : '-';
  }

  getTypeDisplay(type: string): string {
    const roleMap: { [key: string]: string } = {
      assignor: 'Cedente',
      shareholder: 'Cotista',
      provider: 'Prestador de serviço',
    };
    return roleMap[type] || type;
  }

  getPersonTypeDisplay(personType: string | null | undefined): string {
    if (!personType) return '';

    const personTypeMap: { [key: string]: string } = {
      PF: 'Pessoa Física',
      PJ: 'Pessoa Jurídica',
      FUNDO: 'Fundo de Investimento',
    };

    return personTypeMap[personType] || '';
  }

  async cancelInvite() {
    try {
      this.loading = true;
      const finded = this.invites.find((item) => item.id === this.selectedId);
      if (!finded) {
        this.loading = false;
        return;
      }

      await this.api.patch({
        route: `api/v3/registration/invite/${finded.id}/`,
        params: {
          token: finded.token,
        },
        body: {
          canceled: true,
          status: 'canceled',
        },
        token: true,
      });

      this.toast.show('info', 'Sucesso', 'Convite cancelado com sucesso');
      this.handleShowCancelModal();

      // Recarregar dados e summary ao mesmo tempo
      // getData já faz ambas as requisições (listagem e summary) em Promise.all
      await this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao cancelar o convite');
    } finally {
      this.loading = false;
    }
  }

  handleShowCancelModal(id: number = null) {
    if (id !== null) {
      this.selectedId = id;
      this.showCancelModal = true;
    } else {
      this.showCancelModal = false;
      this.selectedId = null;
    }
  }

  toggleModal2() {
    this.showModal2 = !this.showModal2;
    if (!this.showModal2) {
      this.modal2Form.reset();
    }
  }

  async handleModal2Action() {
    if (this.modal2Form.invalid) {
      this.toast.show(
        'error',
        'Erro',
        'Por favor, preencha todos os campos obrigatórios'
      );
      return;
    }

    try {
      // Lógica do modal 2 aqui
      const document = this.modal2Form.value.document;
      // Aqui você pode adicionar a chamada para a API
      // const response = await this.api.get({...});

      this.toast.show('info', 'Sucesso', 'Documento verificado com sucesso');
      this.toggleModal2();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao verificar o documento'
      );
    }
  }

  getModal2Disabled() {
    return this.modal2Form.invalid || this.loading;
  }

  renderInviteLabels() {
    if (!this.invites) return '';
    const finded = this.invites.find((item) => item.id === this.selectedId);

    return finded ? finded.name + ' | ' + finded.document : '';
  }

  getCancelModalMessage() {
    const inviteLabel = this.renderInviteLabels();
    if (!inviteLabel) {
      return 'Você tem certeza que deseja cancelar este convite?';
    }
    return `Você tem certeza que deseja cancelar o convite de ${inviteLabel}?`;
  }

  handleResendInvite(id: number) {
    this.newInvite = true;
    this.editing = true;

    const finded = this.invites.find((item) => item.id === id);

    if (!finded) return;

    this.inviteId = id;

    // Extrair IDs dos agentes se for array de objetos
    let agentIds: number[] = [];
    if (finded.agent) {
      if (Array.isArray(finded.agent) && finded.agent.length > 0) {
        if (
          typeof finded.agent[0] === 'object' &&
          finded.agent[0] !== null &&
          'id' in finded.agent[0]
        ) {
          // Novo formato: array de objetos { id, name }
          agentIds = (finded.agent as Array<{ id: number; name: string }>).map(
            (a) => a.id
          );
        } else if (typeof finded.agent[0] === 'number') {
          // Formato antigo: array de números
          agentIds = finded.agent as number[];
        }
      } else if (typeof finded.agent === 'number') {
        // Formato antigo: número único
        agentIds = [finded.agent];
      }
    }

    // Se o convite tiver person_type, usar; caso contrário, inferir pelo documento
    let personType = (finded as any).person_type || null;

    // Fallback: inferir o tipo pelo tamanho do documento se person_type não existir
    if (!personType && finded.document) {
      const docNumbers = finded.document.replace(/\D/g, '');
      if (docNumbers.length === 11) {
        personType = 'PF'; // CPF tem 11 dígitos
      } else if (docNumbers.length === 14) {
        personType = 'PJ'; // CNPJ tem 14 dígitos
      } else {
        personType = 'PJ'; // Default para PJ em caso de dúvida
      }
    }

    // Atualizar máscara baseado no tipo de pessoa
    if (personType === 'PF') {
      this.documentMask = '000.000.000-00';
      this.form.controls.document.setValidators([
        Validators.required,
        UtilsValidators.cpf,
      ]);
      // Pessoa Física: usar validação UtilsValidators.fullName
      this.form.controls.name.setValidators([
        Validators.required,
        UtilsValidators.fullName,
      ]);
    } else if (personType === 'PJ' || personType === 'FUNDO') {
      this.documentMask = '00.000.000/0000-00';
      this.form.controls.document.setValidators([
        Validators.required,
        UtilsValidators.cnpj,
      ]);
      // Pessoa Jurídica ou Fundo: apenas required (pode conter números na razão social)
      this.form.controls.name.setValidators([Validators.required]);
    } else {
      this.documentMask = 'CPF_CNPJ';
      this.form.controls.document.setValidators([Validators.required]);
      // Sem tipo: apenas required (sem validação de números)
      this.form.controls.name.setValidators([Validators.required]);
    }

    // Montar unified_role a partir do tipo e pessoa_type
    const unifiedRole = personType ? `${finded.type}-${personType}` : null;

    // Ao editar, sempre mostrar os outros campos
    this.showOtherFields = true;
    this.documentVerified = true; // Marcar documento como verificado para exibir os campos
    this.documentInviteError = null; // Limpar erro ao editar
    this.documentExists = true; // Marcar que o documento existe (já que é um convite existente)

    // Preencher os campos do formulário
    this.form.patchValue(
      {
        unified_role: unifiedRole,
        name: finded.name,
        agent: agentIds.length > 0 ? agentIds : null,
        document: finded.document,
        email: finded.email,
        fund: finded.fund_interest_id,
      },
      { emitEvent: false }
    );

    // Atualizar validações
    this.form.controls.document.updateValueAndValidity();
    if (personType === 'PF') {
      this.form.controls.name.updateValueAndValidity();
    }

    // Forçar detecção de mudanças para garantir que os campos sejam atualizados
    this.cdr.detectChanges();
  }

  async resendInvite() {
    const id = this.inviteId;
    this.sendingInvite = true;

    try {
      const finded = this.invites.find((item) => item.id === id);
      if (!finded) return;

      // Extrair role e person_type do unified_role (já preenchido no handleResendInvite)
      let role = finded.type;
      let personType = (finded as any).person_type || null;

      const unifiedRoleValue = this.form.controls.unified_role.value;
      if (unifiedRoleValue) {
        const parsed = this.parseUnifiedRole(unifiedRoleValue);
        if (parsed) {
          role = parsed.role;
          personType = parsed.personType;
        }
      }

      // Se não tiver person_type, inferir pelo documento
      if (!personType && this.form.controls.document.value) {
        const docNumbers = this.form.controls.document.value.replace(/\D/g, '');
        if (docNumbers.length === 11) {
          personType = 'PF';
        } else if (docNumbers.length === 14) {
          personType = 'PJ';
        }
      }

      // Montar data com a mesma estrutura do sendInvite
      const dataPayload: any = {
        name: this.form.controls.name.value,
        email: this.form.controls.email.value,
        document: this.form.controls.document.value,
        role: role,
        person_type: personType,
        fund: this.form.controls.fund.value,
      };

      // Adicionar shareholder_fi se existir
      if (this.form.controls.shareholder_fi?.value !== undefined) {
        dataPayload.shareholder_fi = this.form.controls.shareholder_fi.value;
      }

      // Adicionar agent se existir
      if (
        this.form.controls.agent?.value &&
        Array.isArray(this.form.controls.agent.value) &&
        this.form.controls.agent.value.length > 0
      ) {
        dataPayload.agent = this.form.controls.agent.value;
      }

      // Montar body com a mesma estrutura do sendInvite
      const body: any = {
        token: finded.token,
        data: dataPayload,
        type: role,
        intent: 'register',
      };

      const res = await this.api.post({
        route: 'api/v3/registration/invite/resend/',
        token: true,
        body: body,
      });

      this.toast.show('info', 'Sucesso', 'Convite reenviado com sucesso');
      this.closeModal();

      // Recarregar dados e summary ao mesmo tempo
      // getData já faz ambas as requisições (listagem e summary) em Promise.all
      await this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao reenviar o convite');
    } finally {
      this.sendingInvite = false;
    }
  }

  openDetailsModal(id: number) {
    this.inviteDetails = this.invites.find((item) => item.id === id);
  }

  setPermissions() {
    const permissions = this.authService.mapPermissionsSlugs(
      'invites',
      this.permissionObj
    );

    const isCommonCollaborator = this.authService.isCommonCollaborator();
    const isMaster = this.authService.isMasterUser();

    permissions.forEach((permission, idx) => {
      const key = Object.keys(this.permissionObj)[idx];
      // Permitir canFilter para master e usuários comuns
      if (key === 'canFilter' && (isMaster || isCommonCollaborator)) {
        this.permissionObj[key] = true;
      }
      // Se for colaborador comum, permitir canAdd e as permissões de adicionar por papel
      else if (isCommonCollaborator) {
        if (
          key === 'canAdd' ||
          key === 'canAddAssignor' ||
          key === 'canAddShareholder' ||
          key === 'canAddProvider'
        ) {
          this.permissionObj[key] = true;
        } else {
          this.permissionObj[key] =
            this.authService.verifyPermission(permission);
        }
      } else {
        this.permissionObj[key] = this.authService.verifyPermission(permission);
      }
    });
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    // Mapeamento de labels para os campos
    const fieldLabels: { [key: string]: string } = {
      name: 'Nome / Razão Social',
      email: 'E-mail',
      document: 'CPF / CNPJ',
      status: 'Status',
    };

    // Mapeamento de valores de status para labels amigáveis
    const statusLabels: { [key: string]: string } = {
      pending: 'Pendente',
      sent: 'Enviado',
      viewed: 'Visualizado',
      used: 'Utilizado',
      expired: 'Expirado',
      canceled: 'Cancelado',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        if (fieldLabels[key]) {
          // Para status, usar o label mapeado ao invés do valor bruto
          let displayValue = String(value);
          if (key === 'status' && statusLabels[value]) {
            displayValue = statusLabels[value];
          }

          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${displayValue}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: displayValue,
          });
        }
      }
    });
  }
}
